/**
 * Cosul.
 *
 * Verificarea usoara a ADRESEI aduce zona si regulile ei. Dupa ce exista
 * produse, cotatia `storefront_open` aduce decizia pe cosul REAL: restaurant,
 * acoperire, subventii, taxa si total. Cererea e temporizata la modificari
 * rapide de cantitate, ca un sir de tap-uri sa produca o singura interogare a
 * bucatariei. Checkout-ul recalculeaza si semneaza cotatia definitiva.
 *
 * Ce e nou fata de varianta anterioara, si de ce:
 *  - liniile intrate fara adresa se REVERIFICA aici contra meniului adresei
 *    (acelasi hook ca in meniu), iar cele neonorabile raman vizibile, marcate,
 *    cu buton de scoatere — nu dispar;
 *  - randurile sunt compacte (poza, nume, optiuni, stepper, pret), iar
 *    mentiunile per produs se deschid la cerere: un textarea sub fiecare produs
 *    dubla inaltimea cosului si il facea sa arate a formular;
 *  - „Completeaza comanda": cateva produse din meniu, cu adaugare rapida, ca in
 *    cosul oricarei aplicatii de livrare;
 *  - totalul si butonul de continuare stau intr-o bara LIPITA jos, mereu
 *    vizibila (cea mai citata slabiciune a lui Glovo e totalul ascuns).
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { CORE_ERROR_CODES, MAX_QTY_PER_LINE, formatRon, type QuoteResponse } from "@deliveryapp/shared";

import { fetchStorefront, requestQuote } from "../api/client";
import { useAddressSheet } from "../components/AddressSheetProvider";
import { formatAddress, formatAddressShort } from "../components/AddressForm";
import { useBootstrap } from "../components/BootstrapProvider";
import { cartOfferMinimumMissing, qtyForOffer, useCart, type CartLine } from "../components/CartProvider";
import { minutesFromNow } from "../components/fulfillment";
import { deliveryProgress } from "../components/delivery-progress";
import { IconArrowLeft, IconBike, IconClock, IconEdit, IconPin, IconStore, IconTrash, IconWarning } from "../components/icons";
import { ImageFallback, ResponsiveImage } from "../components/Media";
import { OfferCard, type OfferAddPayload } from "../components/OfferCard";
import { refreshedAddPayload } from "../components/offer-refresh";
import { quotedPriceForLine } from "../components/quote-pricing";
import { recommendedOffers } from "../components/recommendations";
import { EmptyState } from "../components/StateScreen";
import { useToast } from "../components/ToastProvider";
import { Alert, Button, Price, ProgressBar, SectionHeading, Stepper, TextArea } from "../components/ui";
import { useAddressMenu, useCartRevalidation } from "../components/useAddressMenu";
import { toApiError, useLoadable } from "../components/useLoadable";

function CartLineRow({
  line,
  offerQty,
  currentQuote,
  onSetQty,
  onRemove,
  onNotes,
}: {
  line: CartLine;
  offerQty: number;
  currentQuote: QuoteResponse | null;
  onSetQty: (qty: number) => void;
  onRemove: () => void;
  onNotes: (notes: string) => void;
}) {
  const [notesOpen, setNotesOpen] = useState(false);
  const lineMin = typeof line.minQty === "number" ? Math.max(1, line.minQty) : 1;
  const belowMin = offerQty < lineMin;
  const quotedLine = quotedPriceForLine(line, currentQuote);
  const unitPriceGross = quotedLine?.customerUnitPrice ?? quotedLine?.unitPriceGross ?? line.unitPriceGross;
  // O promoție persistată local nu este prezentată ca activă. Prețul barat
  // apare numai dacă serverul tocmai l-a confirmat în cotație.
  const compareAtUnitPrice =
    typeof quotedLine?.compareAtUnitPrice === "number" && quotedLine.compareAtUnitPrice > unitPriceGross
      ? quotedLine.compareAtUnitPrice
      : null;
  const unavailable = line.unavailableAtAddress === true;
  const ceiling = Math.min(line.maxQty ?? MAX_QTY_PER_LINE, MAX_QTY_PER_LINE);

  return (
    <li className={`flex flex-col gap-2 py-3 ${unavailable ? "opacity-90" : ""}`}>
      <div className="flex items-start gap-3">
        <div className="w-16 shrink-0">
          <ResponsiveImage src={line.imageUrl} alt="" ratio="1/1" sizes="4rem" className={unavailable ? "opacity-50" : ""} fallback={<ImageFallback />} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-ink">{line.name}</p>
          {line.optionLabels.length === 0 ? null : <p className="text-xs text-ink-muted">{line.optionLabels.join(" · ")}</p>}
          {line.notes === null || notesOpen ? null : <p className="text-xs italic text-ink-muted">„{line.notes}"</p>}
          {unavailable ? (
            <p className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-danger">
              <IconWarning size={14} /> Nu e disponibil la adresa ta
            </p>
          ) : lineMin > 1 ? (
            <p className={`mt-1 text-xs font-semibold ${belowMin ? "text-danger" : "text-ink-muted"}`}>
              {belowMin ? `Se comandă de la ${lineMin} buc. Mai adaugă ${lineMin - offerQty}.` : `min. ${lineMin} buc.`}
            </p>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {unavailable ? (
              <Button size="sm" variant="danger" icon={<IconTrash size={16} />} onClick={onRemove}>
                Elimină
              </Button>
            ) : (
              <>
                <Stepper
                  size="sm"
                  value={line.qty}
                  name={line.name}
                  min={0}
                  max={ceiling}
                  onDecrease={() => onSetQty(line.qty - 1)}
                  onIncrease={() => onSetQty(line.qty + 1)}
                  decreaseLabel={line.qty <= 1 ? `Scoate ${line.name} din coș` : undefined}
                />
                <button
                  type="button"
                  onClick={() => setNotesOpen((open) => !open)}
                  className="inline-flex min-h-9 items-center gap-1 rounded-control px-2 text-xs font-semibold text-ink-muted hover:text-ink"
                  aria-expanded={notesOpen}
                >
                  <IconEdit size={14} /> {line.notes === null ? "Mențiune" : "Modifică mențiunea"}
                </button>
              </>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <Price amount={unitPriceGross * line.qty} className="block text-sm font-bold text-ink" />
          {compareAtUnitPrice === null ? null : (
            <Price amount={compareAtUnitPrice * line.qty} className="block text-xs text-ink-muted line-through" />
          )}
          {line.qty > 1 ? <span className="block text-xs text-ink-muted">{`${line.qty} × ${formatRon(unitPriceGross)}`}</span> : null}
        </div>
      </div>
      {notesOpen ? (
        <TextArea
          label={`Mențiuni pentru ${line.name}`}
          quiet
          rows={2}
          maxLength={300}
          autoFocus
          value={line.notes ?? ""}
          onChange={(event) => onNotes(event.target.value)}
          onBlur={() => setNotesOpen(false)}
        />
      ) : null}
    </li>
  );
}

export function Cart() {
  const { marketKey, config, t } = useBootstrap();
  const { session, itemCount, setQty, setLineNotes, clearLines, addLine, removeUnavailableLines, hasUnavailableLines, setVoucherCode } = useCart();
  const [voucherDraft, setVoucherDraft] = useState(session.voucherCode ?? "");
  const { toast } = useToast();
  const navigate = useNavigate();
  const addressSheet = useAddressSheet();

  const storefrontSlug = session.storefrontSlug;
  const storefrontLink = storefrontSlug === null ? "/" : `/vitrina/${encodeURIComponent(storefrontSlug)}`;

  const addressMenu = useAddressMenu(storefrontSlug, true);
  const where = session.pickup !== null ? "la locația aleasă" : "la adresa ta";
  useCartRevalidation(addressMenu, (result) => {
    if (result.unavailable.length > 0) {
      toast({
        key: "revalidare",
        tone: "warning",
        title:
          result.unavailable.length === 1
            ? `„${result.unavailable[0]}" nu e disponibil ${where}`
            : `${result.unavailable.length} produse nu sunt disponibile ${where}`,
        durationMs: 6000,
      });
    } else if (result.changed > 0) {
      toast({ key: "revalidare", tone: "info", title: `Coșul a fost actualizat pentru ${session.pickup !== null ? "locația aleasă" : "adresa ta"}` });
    }
  });

  // Meniul pentru „Completeaza comanda": cel al adresei cand exista, altfel
  // cel generic. Doar cand cosul are ceva — pe cos gol trimitem la meniu.
  const genericStorefront = useLoadable(
    (signal) => (storefrontSlug === null || itemCount === 0 ? Promise.resolve(null) : fetchStorefront(marketKey, storefrontSlug, signal)),
    [marketKey, storefrontSlug, itemCount === 0],
  );
  const menus = addressMenu.menu?.menus ?? genericStorefront.data?.menus ?? [];
  const cartOfferIds = useMemo(() => new Set(session.lines.map((line) => line.offerId)), [session.lines]);
  const suggestions = useMemo(() => recommendedOffers(menus, cartOfferIds, 6), [menus, cartOfferIds]);
  const qtyByOffer = useMemo(() => {
    const map = new Map<string, number>();
    for (const line of session.lines) map.set(line.offerId, (map.get(line.offerId) ?? 0) + line.qty);
    return map;
  }, [session.lines]);

  // Cheia descrie TOATA intrarea cotatiei, nu doar cantitatile.
  const quoteInputKey = useMemo(
    () =>
      JSON.stringify({
        marketKey,
        storefrontSlug: session.storefrontSlug,
        address: session.address === null ? null : { lat: session.address.lat, lng: session.address.lng, city: session.address.city },
        pickup: session.pickup?.vendorId ?? null,
        lines: session.lines
          .filter((line) => line.unavailableAtAddress !== true)
          .map((line) => ({ lineId: line.lineId, offerId: line.offerId, qty: line.qty, notes: line.notes, selectedOptions: line.selectedOptions })),
        voucherCode: session.voucherCode,
      }),
    [marketKey, session.storefrontSlug, session.address, session.lines, session.voucherCode],
  );
  const [debouncedQuoteInputKey, setDebouncedQuoteInputKey] = useState(quoteInputKey);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuoteInputKey(quoteInputKey), 350);
    return () => window.clearTimeout(timer);
  }, [quoteInputKey]);

  const quotableLines = session.lines.filter((line) => line.unavailableAtAddress !== true);
  const quote = useLoadable<{ requestKey: string; response: QuoteResponse | null; error: ReturnType<typeof toApiError> | null } | null>(
    async (signal) => {
      if ((session.address === null && session.pickup === null) || session.storefrontSlug === null || quotableLines.length === 0 || addressMenu.outOfCoverage) {
        return Promise.resolve(null);
      }
      try {
        const response = await requestQuote(
          marketKey,
          {
            storefrontSlug: session.storefrontSlug,
            items: quotableLines.map((line) => ({
              offerId: line.offerId,
              qty: line.qty,
              notes: line.notes,
              selectedOptions: line.selectedOptions.length > 0 ? line.selectedOptions : undefined,
            })),
            ...(session.pickup !== null
              ? { fulfillment: { type: "pickup" as const, vendorId: session.pickup.vendorId } }
              : session.address === null
                ? {}
                : { dropoff: { lat: session.address.lat, lng: session.address.lng, city: session.address.city } }),
            // `storefront_open` permite estimarea si SUB minim; exact aici avem
            // nevoie sa stim cat lipseste. `checkout` il impune la pasul urmator.
            reason: "storefront_open",
            voucherCode: session.voucherCode,
          },
          signal,
        );
        return { requestKey: debouncedQuoteInputKey, response, error: null };
      } catch (error: unknown) {
        if (signal.aborted) throw error;
        return { requestKey: debouncedQuoteInputKey, response: null, error: toApiError(error) };
      }
    },
    [debouncedQuoteInputKey, addressMenu.outOfCoverage],
  );

  const currentQuoteResult =
    debouncedQuoteInputKey === quoteInputKey && quote.data?.requestKey === quoteInputKey ? quote.data : null;
  const currentQuote = currentQuoteResult?.response ?? null;
  const currentQuoteError = currentQuoteResult?.error ?? null;
  const zone = addressMenu.preview?.zone ?? null;
  const payableSubtotal = currentQuote === null ? null : Math.max(0, currentQuote.totals.subtotal - currentQuote.totals.platformSubsidyTotal);

  // La ridicare nu exista zona: nici minim de zona, nici prag de livrare gratuita.
  const progress =
    currentQuote === null || payableSubtotal === null
      ? null
      : session.pickup !== null
        ? deliveryProgress({ subtotal: payableSubtotal, deliveryFee: 0, minOrderValue: 0, freeDeliveryThreshold: null })
        : zone === null
          ? null
          : deliveryProgress({
          subtotal: payableSubtotal,
          deliveryFee: currentQuote.totals.deliveryFee,
          minOrderValue: zone.minOrderValue,
          freeDeliveryThreshold: zone.freeDeliveryThreshold,
        });
  const lineMinimumMissing = cartOfferMinimumMissing(quotableLines);
  const deliveryBlocked =
    (session.pickup === null && (session.address === null || addressMenu.previewState !== "ready" || zone === null)) ||
    (session.pickup !== null && addressMenu.outOfCoverage) ||
    quote.state !== "ready" ||
    currentQuote === null ||
    currentQuote.coverage.status !== "full" ||
    progress === null ||
    !progress.minimumReached ||
    lineMinimumMissing ||
    hasUnavailableLines;
  const quoteAddressOutOfCoverage = currentQuoteError !== null && currentQuoteError.code === CORE_ERROR_CODES.ADDRESS_OUT_OF_COVERAGE;

  const addSuggestion = (offer: (typeof suggestions)[number], payload: OfferAddPayload): void => {
    const addressKey = addressMenu.addressKey;
    const verified = addressMenu.menu !== null;
    const refreshed = verified ? refreshedAddPayload(offer, payload) : payload;
    if (refreshed === null) {
      toast({ key: `adaugat-${offer.offerId}`, tone: "warning", title: "Produsul nu e disponibil la adresa ta" });
      return;
    }
    addLine(
      {
        lineId: refreshed.lineId,
        offerId: offer.offerId,
        skuId: offer.skuId,
        name: offer.name,
        unitPriceGross: refreshed.unitPriceGross,
        compareAtUnitPrice: refreshed.compareAtUnitPrice,
        notes: refreshed.notes ?? null,
        imageUrl: refreshed.imageUrl,
        selectedOptions: refreshed.options.map((option) => ({ groupId: option.groupId, optionId: option.optionId, priceDelta: option.priceDelta })),
        optionLabels: refreshed.options.map((option) => `${option.groupName}: ${option.optionName}`),
        maxQty: offer.maxQty,
        minQty: offer.minQty,
        verifiedForAddressKey: verified ? addressKey : null,
      },
      refreshed.qty,
      offer.maxQty,
    );
    toast({ key: `adaugat-${offer.offerId}`, tone: "success", title: `${offer.name} adăugat în coș` });
  };

  if (itemCount === 0) {
    return (
      <EmptyState
        title={t("cart.empty_title", "Coșul este gol")}
        description={t("cart.empty_description", "Alege produsele din meniu, apoi revino aici.")}
        action={<Button onClick={() => navigate(storefrontLink)}>{storefrontSlug === null ? "Vezi restaurantele" : "Deschide meniul"}</Button>}
      />
    );
  }

  const address = session.address;
  const pickup = session.pickup;
  const ctaLabel =
    address === null && pickup === null
      ? "Adaugă adresa"
      : hasUnavailableLines
        ? "Scoate produsele indisponibile"
        : progress !== null && !progress.minimumReached
          ? `Mai adaugă ${formatRon(progress.remainingToMinimum)}`
          : "Continuă spre finalizare";
  const ctaAction = (): void => {
    if (address === null && pickup === null) {
      addressSheet.open();
      return;
    }
    if (hasUnavailableLines) {
      removeUnavailableLines();
      return;
    }
    if (progress !== null && !progress.minimumReached) {
      navigate(storefrontLink);
      return;
    }
    navigate("/finalizare");
  };
  const ctaDisabled = address !== null && !hasUnavailableLines && (progress === null || progress.minimumReached) && deliveryBlocked;

  return (
    <div className="flex flex-col gap-5 pb-28">
      <div className="flex items-center justify-between gap-3">
        <div>
          <SectionHeading level={1}>{t("cart.title", "Coșul tău")}</SectionHeading>
          {session.storefrontName === null ? null : <p className="text-sm text-ink-muted">{session.storefrontName}</p>}
        </div>
        <Link to={storefrontLink} className="inline-flex items-center gap-1 text-sm font-semibold text-brand-on-surface hover:underline">
          <IconArrowLeft size={16} /> Adaugă produse
        </Link>
      </div>

      {hasUnavailableLines ? (
        <Alert
          tone="warning"
          title="Unele produse nu sunt disponibile la adresa ta"
          icon={<IconWarning size={18} />}
          actions={
            <>
              <Button size="sm" onClick={removeUnavailableLines}>
                Scoate-le din coș
              </Button>
              <Button size="sm" variant="secondary" onClick={() => addressSheet.open()}>
                Schimbă adresa
              </Button>
            </>
          }
        >
          Le vezi marcate mai jos. Restul comenzii rămâne neschimbat.
        </Alert>
      ) : null}

      <ul className="da-card divide-y divide-line px-4">
        {session.lines.map((line) => (
          <CartLineRow
            key={line.lineId}
            line={line}
            offerQty={qtyForOffer(session.lines, line.offerId)}
            currentQuote={currentQuote}
            onSetQty={(qty) => setQty(line.lineId, qty, line.maxQty)}
            onRemove={() => setQty(line.lineId, 0)}
            onNotes={(notes) => setLineNotes(line.lineId, notes)}
          />
        ))}
      </ul>

      {suggestions.length === 0 ? null : (
        <section className="flex flex-col gap-3" aria-labelledby="completeaza-titlu">
          <h2 id="completeaza-titlu" className="text-lg font-bold text-ink">
            Completează comanda
          </h2>
          <ul className="da-rail da-rail-bleed pb-2 [&>li]:w-64 sm:[&>li]:w-60">
            {suggestions.map((offer) => (
              <OfferCard
                key={offer.offerId}
                offer={offer}
                qtyInCart={qtyByOffer.get(offer.offerId) ?? 0}
                addressPending={addressMenu.menu === null}
                onAdd={(payload) => addSuggestion(offer, payload)}
                onDecrease={() => {
                  const next = (qtyByOffer.get(offer.offerId) ?? 0) - 1;
                  setQty(offer.offerId, next < offer.minQty ? 0 : next, offer.maxQty);
                }}
              />
            ))}
          </ul>
        </section>
      )}

      {/* Livrarea: adresa + ce stim despre ea. */}
      {address === null && pickup === null ? (
        <button
          type="button"
          onClick={() => addressSheet.open()}
          className="da-card da-card-link da-tap flex w-full items-center gap-3 p-3.5 text-left"
        >
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-on-surface">
            <IconPin size={20} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-ink">{config.fulfillment?.pickup === true ? "Livrare sau ridicare?" : "Spune-ne unde livrăm"}</span>
            <span className="block text-xs text-ink-muted">
              {config.fulfillment?.pickup === true
                ? "Alege adresa de livrare sau locația din care ridici comanda."
                : "Verificăm zona, taxa și restaurantul înainte de finalizare."}
            </span>
          </span>
          <span className="shrink-0 rounded-control bg-brand px-3 py-2 text-sm font-bold text-brand-contrast">Adaugă</span>
        </button>
      ) : (
        <section className="da-card overflow-hidden" aria-labelledby="livrare-cos-titlu">
          <button type="button" onClick={() => addressSheet.open()} className="da-tap flex w-full items-center gap-3 p-3.5 text-left hover:bg-surface-hover">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-on-surface">
              {pickup !== null ? <IconStore size={20} /> : <IconPin size={20} />}
            </span>
            {pickup !== null ? (
              <span className="min-w-0 flex-1">
                <span id="livrare-cos-titlu" className="da-clamp-1 block text-sm font-bold text-ink">{`Ridici de la ${pickup.name}`}</span>
                <span className="da-clamp-1 block text-xs text-ink-muted">{pickup.address}</span>
              </span>
            ) : address !== null ? (
              <span className="min-w-0 flex-1">
                <span id="livrare-cos-titlu" className="da-clamp-1 block text-sm font-bold text-ink">{`${formatAddressShort(address)}, ${address.city}`}</span>
                <span className="da-clamp-1 block text-xs text-ink-muted">{formatAddress(address)}</span>
              </span>
            ) : null}
            <span className="shrink-0 text-sm font-bold text-brand-on-surface">Schimbă</span>
          </button>

          <div className="border-t border-line p-3.5">
            {pickup !== null ? (
              addressMenu.outOfCoverage ? (
                <div className="flex items-start gap-2 text-sm">
                  <IconWarning size={18} className="mt-0.5 shrink-0 text-warning" />
                  <p className="text-ink">
                    <span className="font-bold">Locația nu primește acum comenzi de ridicare.</span> Alege altă locație sau livrarea.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    {currentQuote === null ? (
                      <span className="da-shimmer block h-3.5 w-1/3 rounded" aria-hidden="true" />
                    ) : (
                      <span className="inline-flex items-center gap-1 font-semibold text-ink">
                        <IconClock size={16} /> {`gata în ~${minutesFromNow(currentQuote.eta.deliveryFrom)} min`}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-ink-muted">
                      <IconStore size={16} /> fără taxă de livrare
                    </span>
                  </div>
                  {currentQuoteError === null ? null : (
                    <Alert
                      tone={currentQuoteError.code === CORE_ERROR_CODES.NO_VENDOR_AVAILABLE ? "warning" : "error"}
                      title={currentQuoteError.code === CORE_ERROR_CODES.NO_VENDOR_AVAILABLE ? "Locația nu poate prelua comanda acum" : "Nu putem confirma bucătăria pentru acest coș"}
                      actions={currentQuoteError.retryable ? <Button size="sm" variant="secondary" onClick={quote.reload}>Încearcă din nou</Button> : undefined}
                    >
                      {currentQuoteError.message}
                    </Alert>
                  )}
                </div>
              )
            ) : addressMenu.outOfCoverage ? (
              <div className="flex items-start gap-2 text-sm">
                <IconWarning size={18} className="mt-0.5 shrink-0 text-warning" />
                <p className="text-ink">
                  <span className="font-bold">{addressMenu.previewError?.code === "NO_VENDOR_AVAILABLE" ? "Momentan nu putem livra la adresa ta." : "Adresa este în afara zonei de livrare."}</span>{" "}
                  Alege altă adresă și verificăm imediat.
                </p>
              </div>
            ) : addressMenu.previewState === "error" && addressMenu.previewError !== null ? (
              <div className="flex flex-wrap items-center gap-2 text-sm text-ink">
                <span>Nu am putut verifica livrarea: {addressMenu.previewError.message}</span>
                {addressMenu.previewError.retryable ? (
                  <Button size="sm" variant="secondary" onClick={addressMenu.reload}>
                    Încearcă din nou
                  </Button>
                ) : null}
              </div>
            ) : zone === null || addressMenu.previewState === "loading" ? (
              <div className="flex flex-col gap-2" aria-busy="true">
                <span className="da-shimmer block h-3.5 w-2/3 rounded" />
                <span className="da-shimmer block h-3.5 w-1/3 rounded" />
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="inline-flex items-center gap-1 font-semibold text-ink">
                    <IconClock size={16} /> ~{addressMenu.preview?.eta.totalMinutes} min
                  </span>
                  <span className="inline-flex items-center gap-1 text-ink-muted">
                    <IconBike size={16} />
                    {currentQuote === null
                      ? zone.deliveryFee === 0 ? "livrare gratuită" : `livrare ${formatRon(zone.deliveryFee)}`
                      : currentQuote.totals.deliveryFee === 0 ? "livrare gratuită" : `livrare ${formatRon(currentQuote.totals.deliveryFee)}`}
                  </span>
                  {currentQuote === null ? null : <span className="text-ink-muted">{`pregătește ${currentQuote.seller.displayName}`}</span>}
                </div>

                {progress === null ? null : !progress.minimumReached ? (
                  <div className="rounded-control bg-brand-soft p-3">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-bold text-ink">{`Mai adaugă ${formatRon(progress.remainingToMinimum)} pentru comanda minimă`}</span>
                      <span className="text-ink-muted">{`min. ${formatRon(zone.minOrderValue)}`}</span>
                    </div>
                    <div className="mt-2">
                      <ProgressBar percent={progress.minimumPercent} label="Progres către comanda minimă" />
                    </div>
                  </div>
                ) : progress.remainingToFreeDelivery !== null && progress.remainingToFreeDelivery > 0 ? (
                  <div>
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-semibold text-ink">{`Mai adaugă ${formatRon(progress.remainingToFreeDelivery)} și livrarea e gratuită`}</span>
                    </div>
                    <div className="mt-2">
                      <ProgressBar percent={progress.freeDeliveryPercent ?? 0} label="Progres către livrare gratuită" />
                    </div>
                  </div>
                ) : progress.remainingToFreeDelivery === 0 ? (
                  <p className="text-sm font-bold text-success">✓ Ai deblocat livrarea gratuită</p>
                ) : null}

                {currentQuoteError === null ? null : (
                  <Alert
                    tone={quoteAddressOutOfCoverage ? "warning" : "error"}
                    title={quoteAddressOutOfCoverage ? "Adresa nu mai este în zona de livrare" : "Nu putem confirma bucătăria pentru acest coș"}
                    actions={currentQuoteError.retryable ? <Button size="sm" variant="secondary" onClick={quote.reload}>Încearcă din nou</Button> : undefined}
                  >
                    {currentQuoteError.message}
                  </Alert>
                )}
              </div>
            )}
          </div>

          {/* Voucher valoric (ADR-032): compensare de la suport sau cod promoțional.
              Cotația se reface cu codul; un cod invalid apare ca eroare, nu ca reducere lipsă. */}
          {(session.address === null && session.pickup === null) || quotableLines.length === 0 ? null : (
            <form
              className="flex flex-wrap items-end gap-2 border-t border-line p-3.5"
              onSubmit={(event) => {
                event.preventDefault();
                setVoucherCode(voucherDraft);
              }}
            >
              <label className="flex min-w-[10rem] flex-1 flex-col gap-1 text-xs font-semibold text-ink-muted">
                Cod de voucher
                <input
                  className="da-input min-h-11 rounded-control border border-line px-3 text-sm font-mono uppercase text-ink"
                  value={voucherDraft}
                  onChange={(event) => setVoucherDraft(event.target.value.toUpperCase())}
                  placeholder="ex. AB3K-9QRT"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={32}
                />
              </label>
              <Button type="submit" size="sm" variant="secondary" disabled={voucherDraft.trim().length === 0 || voucherDraft.trim().toUpperCase() === (session.voucherCode ?? "")}>
                Aplică
              </Button>
              {session.voucherCode === null ? null : (
                <Button type="button" size="sm" variant="ghost" onClick={() => { setVoucherDraft(""); setVoucherCode(null); }}>
                  Renunță la voucher
                </Button>
              )}
              {currentQuote?.voucher ? (
                <p className="basis-full text-xs text-success">
                  {`Voucherul ${currentQuote.voucher.code} scade ${(currentQuote.voucher.appliedMinor / 100).toFixed(2)} lei din această comandă.`}
                </p>
              ) : null}
            </form>
          )}

          {currentQuote === null ? null : (
            <dl className="flex flex-col gap-1.5 border-t border-line p-3.5 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-ink-muted">Produse</dt>
                <dd><Price amount={currentQuote.totals.subtotal} /></dd>
              </div>
              {currentQuote.totals.platformSubsidyTotal === 0 ? null : (
                <div className="flex items-center justify-between gap-3 text-success">
                  <dt>Reducere</dt>
                  <dd><Price amount={-currentQuote.totals.platformSubsidyTotal} /></dd>
                </div>
              )}
              {currentQuote.totals.discountTotal === 0 ? null : (
                <div className="flex items-center justify-between gap-3 text-success">
                  <dt>{`Voucher${currentQuote.voucher ? ` ${currentQuote.voucher.code}` : ""}`}</dt>
                  <dd><Price amount={-currentQuote.totals.discountTotal} /></dd>
                </div>
              )}
              {currentQuote.totals.packagingFee === 0 ? null : (
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-ink-muted">Ambalaje</dt>
                  <dd><Price amount={currentQuote.totals.packagingFee} /></dd>
                </div>
              )}
              {currentQuote.totals.smallOrderFee === 0 ? null : (
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-ink-muted">Taxă pentru comandă mică</dt>
                  <dd><Price amount={currentQuote.totals.smallOrderFee} /></dd>
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <dt className="text-ink-muted">Livrare</dt>
                <dd>{currentQuote.totals.deliveryFee === 0 ? "Gratuit" : <Price amount={currentQuote.totals.deliveryFee} />}</dd>
              </div>
              <div className="flex items-center justify-between gap-3 border-t border-line pt-2 text-base font-extrabold text-ink">
                <dt>Total estimat</dt>
                <dd><Price amount={currentQuote.totals.total} /></dd>
              </div>
            </dl>
          )}
        </section>
      )}

      <div className="flex justify-end">
        <Button variant="ghost" size="sm" icon={<IconTrash size={16} />} onClick={clearLines}>
          Golește coșul
        </Button>
      </div>

      {/* Bara lipita: totalul si pasul urmator, mereu la indemana. */}
      <div className="da-bottom-bar">
        <div className="mx-auto flex max-w-app items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1 leading-tight">
            <span className="block text-xs font-semibold uppercase tracking-wide text-ink-muted">
              {currentQuote === null ? "Produse" : "Total estimat"}
            </span>
            <Price amount={currentQuote?.totals.total ?? session.lines.filter((line) => line.unavailableAtAddress !== true).reduce((sum, line) => sum + line.unitPriceGross * line.qty, 0)} className="block text-lg font-extrabold text-ink" />
          </div>
          <Button size="lg" onClick={ctaAction} disabled={ctaDisabled} className="min-w-[55%] sm:min-w-64">
            {ctaLabel}
          </Button>
        </div>
      </div>

    </div>
  );
}
